import type { ForwardedRef, ForwardRefExoticComponent } from 'react';
import { forwardRef, useContext } from 'react';
import type { PropsOf, SerializedStyles } from '@emotion/react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { shouldForwardProp } from '@utils/styledProps';
import { navigationClickHandler } from '@utils/navigation-handler';
import { color, focus, spacer, text } from '@tokens';
import { Icon } from '@components/Icon/Icon';
import { externalRel } from '@common/ButtonOrLink';
import type {
  ButtonOrLinkProps,
  ButtonOrLinkElement,
} from '@common/ButtonOrLink';
import { HotsauceContext } from '@providers/hotsauceContext';

/**
 * Kinds of link available
 */
export type LinkKind = 'inline' | 'standalone';

/**
 * Styles for each kind of link
 * @private
 */
const linkStyles: Record<LinkKind, SerializedStyles> = {
  standalone: css`
    ${text('link')};
    color: ${color.link};
    outline: none;
    text-decoration-line: underline;

    &:hover {
      text-decoration-thickness: 2px;
    }
  `,
  inline: css`
    color: inherit;
    font-size: inherit;
    line-height: inherit;
    outline: none;
    text-decoration-line: underline;

    &:hover {
      text-decoration-thickness: 2px;
    }
  `,
};

/**
 * Prop types for the Link component
 */
export type LinkProps<T extends ButtonOrLinkElement = ButtonOrLinkElement> =
  ButtonOrLinkProps<T> & {
    /**
     * Kind of link used.
     */
    kind: LinkKind;

    /**
     * Styles override
     */
    className?: string;

    /**
     * Element content.
     */
    children: string;
  };

type PassedLinkProps<T extends ButtonOrLinkElement> = Omit<
  LinkProps<T>,
  'as' | 'children'
>;

/**
 * List of props to use in the styled component
 * @private
 */
const styledLinkPropNames = ['kind'] as const;

/**
 * Link styled component
 * @private
 */
const StyledLink = styled('a', {
  shouldForwardProp: shouldForwardProp(styledLinkPropNames),
})<PassedLinkProps<'a'>>`
  ${({ kind }) => linkStyles[kind]}

  &:focus {
    ${focus()}
  }
`;

const StyledButton = styled('button', {
  shouldForwardProp: shouldForwardProp(styledLinkPropNames),
})<PassedLinkProps<'button'>>`
  display: inline;
  border: none;
  background-color: transparent;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-offset: 2px;

  ${({ kind }) => linkStyles[kind]}

  &:focus {
    ${focus()}
  }

  &:hover {
    text-decoration-thickness: 2px;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${color.text_disabled};
    outline: none;
    text-decoration: none;
  }
`;

/**
 * Render function for Link component.
 * @see Link
 * @private
 */
const LinkRender = <T extends ButtonOrLinkElement = ButtonOrLinkElement>(
  { as, children, ...props }: LinkProps<T>,
  ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
): JSX.Element => {
  const { onNavigate } = useContext(HotsauceContext) ?? {};

  // button case
  if (as === 'button') {
    const { isDisabled, ...buttonProps } = props as PassedLinkProps<'button'>;
    return (
      <StyledButton
        {...buttonProps}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        disabled={isDisabled}
      >
        {children}
      </StyledButton>
    );
  }

  const { isExternal, rel, ...linkProps } = props as PassedLinkProps<'a'>;
  const onClick = (event) =>
    navigationClickHandler({
      event,
      isExternal,
      linkProps: linkProps as PropsOf<'a'>,
      onNavigate,
    });

  return (
    <StyledLink
      {...linkProps}
      ref={ref as ForwardedRef<HTMLAnchorElement>}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? externalRel(rel) : undefined}
      onClick={onClick}
    >
      {children}
      {isExternal && (
        <Icon
          icon="open_new_window"
          inline
          css={css`
            margin: 0 ${spacer.x0};
          `}
        />
      )}
    </StyledLink>
  );
};

/**
 * Link component, used for navigation. Either standalone or within a sentence.
 *
 * {@link https://zeroheight.com/76c439891/p/69cbd3-link Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-link--link Code documentation}
 *
 * @example
 * <Link href="#" kind="standalone">Text link</Link>
 */
export const Link = forwardRef(
  LinkRender,
) as ForwardRefExoticComponent<LinkProps>;

Link.defaultProps = {
  as: 'a',
};

Link.displayName = 'Link';
